import type { AlignedPoint } from '../../utils/comparison/alignStreams';
import type { LapOverlaySeries } from '../../utils/comparison/lapOverlaySeries';

export const CHART_VIEW_W = 800;

export interface ChartPlotLayout {
  ml: number;
  mt: number;
  plotW: number;
  plotH: number;
  xMax: number;
  yMax: number;
  viewH: number;
}

/** Build an SVG path for one aligned metric series; gaps break the subpath. */
export function buildAlignedMetricPath(
  aligned: AlignedPoint[],
  key: 'valueA' | 'valueB',
  layout: ChartPlotLayout,
): string {
  const { ml, mt, plotW, plotH, xMax, yMax } = layout;
  let d = '';
  let started = false;

  for (const p of aligned) {
    const v = p[key];
    if (v == null || !isFinite(v)) {
      started = false;
      continue;
    }
    const x = ml + (p.x / xMax) * plotW;
    const y = mt + plotH - (v / yMax) * plotH;
    if (!started) {
      d += `M${x},${y}`;
      started = true;
    } else {
      d += ` L${x},${y}`;
    }
  }

  return d;
}

export function buildDualChartLayout(
  aligned: AlignedPoint[],
  height: number,
): ChartPlotLayout {
  const vals = aligned.flatMap(p => [p.valueA, p.valueB]).filter((v): v is number => v != null && isFinite(v));
  const yMax = Math.max(...vals, 1) * 1.1;
  const xMax = aligned[aligned.length - 1]?.x || 1;
  const viewH = height;
  const plotH = height - 40;
  return { ml: 48, mt: 16, plotW: CHART_VIEW_W - 48 - 8, plotH, xMax, yMax, viewH };
}

export function buildMultiLapChartLayout(series: LapOverlaySeries[], height: number): ChartPlotLayout {
  const allY = series.flatMap(s => s.points.map(p => p.y)).filter(v => isFinite(v));
  const yMax = Math.max(...allY, 1) * 1.1;
  const xMax = Math.max(...series.flatMap(s => s.points.map(p => p.x)), 1);
  const viewH = height;
  const plotH = height - 40;
  return { ml: 48, mt: 16, plotW: CHART_VIEW_W - 48 - 8, plotH, xMax, yMax, viewH };
}

/** Map screen X inside SVG to data-space X (touch + pointer). */
export function dataXFromClientX(svg: SVGSVGElement, clientX: number, layout: ChartPlotLayout): number | null {
  const rect = svg.getBoundingClientRect();
  if (rect.width <= 0) return null;
  const vx = ((clientX - rect.left) / rect.width) * CHART_VIEW_W;
  const relX = vx - layout.ml;
  if (relX < 0 || relX > layout.plotW) return null;
  return (relX / layout.plotW) * layout.xMax;
}

/** @deprecated Use dataXFromClientX */
export const dataXFromMouseEvent = dataXFromClientX;

export function nearestAlignedIndex(aligned: AlignedPoint[], dataX: number): number {
  if (!aligned.length) return 0;
  let best = 0;
  let bestDist = Math.abs(aligned[0].x - dataX);
  for (let i = 1; i < aligned.length; i++) {
    const d = Math.abs(aligned[i].x - dataX);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/** Nearest point index per lap series at shared lap-time X. */
export function sampleSeriesAtDataX(
  series: LapOverlaySeries[],
  dataX: number,
): Array<{ series: LapOverlaySeries; y: number; x: number }> {
  const out: Array<{ series: LapOverlaySeries; y: number; x: number }> = [];
  for (const s of series) {
    if (!s.points.length) continue;
    let best = 0;
    let bestDist = Math.abs(s.points[0].x - dataX);
    for (let i = 1; i < s.points.length; i++) {
      const d = Math.abs(s.points[i].x - dataX);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    const pt = s.points[best];
    if (pt && isFinite(pt.y)) out.push({ series: s, y: pt.y, x: pt.x });
  }
  return out;
}

export function chartXFromDataX(dataX: number, layout: ChartPlotLayout): number {
  return layout.ml + (dataX / layout.xMax) * layout.plotW;
}

export function chartYFromValue(value: number, layout: ChartPlotLayout): number {
  return layout.mt + layout.plotH - (value / layout.yMax) * layout.plotH;
}

export function formatChartX(x: number, alignment: 'time' | 'distance'): string {
  if (alignment === 'distance') {
    return x >= 1000 ? `${(x / 1000).toFixed(2)} km` : `${Math.round(x)} m`;
  }
  const s = Math.max(0, Math.round(x));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function formatMetricValue(metric: string, value: number): string {
  if (metric === 'speed') return `${value.toFixed(1)} km/h`;
  if (metric === 'altitude') return `${Math.round(value)} m`;
  if (metric === 'heart_rate' || metric === 'cadence') return `${Math.round(value)}`;
  return `${Math.round(value)} W`;
}
